const popupName = 'rickymau-pos-ticket';
const popupFragment = '#pos-ticket-popup';
const manualPopupFragment = '#pos-ticket-popup-manual';
const popupRequests = new WeakMap();
const printControllers = new WeakMap();

export function openTicketPopup(event, target = window) {
    if (
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
    ) {
        return false;
    }

    const url = new URL(event.currentTarget.href);
    if (
        url.origin !== target.location.origin ||
        !/^\/pos\/(?:invoices\/[^/]+\/print|phone-orders\/[^/]+\/slip)$/.test(
            url.pathname,
        )
    ) {
        return false;
    }

    // The popup identity is the document: path plus its own query (for example the slip revision). The signing
    // parameters change every time the link is rendered, so they are left out or nothing would ever be reused.
    const identity = new URLSearchParams(url.search);
    identity.delete('expires');
    identity.delete('signature');
    identity.sort();
    const popupKey = `${url.pathname}?${identity.toString()}`;
    const requests = popupRequests.get(target) ?? new Map();
    const existing = requests.get(popupKey);
    if (existing && !existing.closed) {
        existing.focus?.();
        event.preventDefault();
        return true;
    }

    let popup;
    try {
        // Keep a handle until opener isolation is established, before receipt navigation.
        popup = target.open(
            'about:blank',
            '_blank',
            'popup,width=420,height=720',
        );
        if (!popup) {
            url.hash = manualPopupFragment;
            event.currentTarget.href = url.href;
            return false;
        }
        popup.opener = null;
        popup.name = popupName;
        url.hash =
            existing || url.hash === manualPopupFragment
                ? manualPopupFragment
                : popupFragment;
        popup.location.replace(url.href);
        requests.set(popupKey, popup);
        popupRequests.set(target, requests);
    } catch {
        popup?.close();
        url.hash = manualPopupFragment;
        event.currentTarget.href = url.href;
        return false;
    }

    event.preventDefault();
    return true;
}

export function installTicketPrint(target = window) {
    if (printControllers.has(target)) {
        return printControllers.get(target);
    }

    const document = target.document;
    const copies = (document.body?.dataset.ticketCopies ?? 'customer').split(
        ',',
    );
    // A generic key (for example order id + revision) wins; the invoice id stays supported.
    const invoice =
        document.body?.dataset.ticketKey ??
        document.body?.dataset.ticketInvoice;
    const states = new Map();
    const buttons = [
        ...(document.querySelectorAll?.('[data-ticket-copy]') ?? []),
    ];
    const label = document.querySelector?.('[data-ticket-copy-label]');
    const status = document.querySelector?.('[data-ticket-print-status]');
    const copyNames = new Map(
        buttons.map((button) => [
            button.dataset.ticketCopy,
            button.dataset.ticketCopyName,
        ]),
    );
    const copyName = (copy) => copyNames.get(copy) || copy;
    let active = null;
    let automaticIndex = 0;
    let automatic = true;

    const storageKey = (copy) => `pos-ticket:${invoice}:${copy}`;
    for (const copy of copies) {
        let stored = null;
        try {
            stored = invoice
                ? target.sessionStorage?.getItem(storageKey(copy))
                : null;
        } catch {
            // Private browsing may disable storage; in-memory guards still apply.
        }
        states.set(
            copy,
            stored === 'failed' ? 'failed' : stored ? 'requested' : 'pending',
        );
    }

    const remember = (copy, state) => {
        states.set(copy, state);
        try {
            if (invoice) {
                target.sessionStorage?.setItem(storageKey(copy), state);
            }
        } catch {
            // Printing remains available when session storage is unavailable.
        }
    };

    const updateControls = () => {
        for (const button of buttons) {
            button.disabled = active !== null;
            const state = states.get(button.dataset.ticketCopy);
            const stateLabels = {
                pending: 'pendiente',
                printing: 'diálogo en curso',
                requested: 'solicitud enviada; comprobar papel',
                failed: 'solicitud fallida',
            };
            button.textContent = `Imprimir copia ${copyName(button.dataset.ticketCopy)} (${stateLabels[state]})`;
        }
    };

    const fitOrderNumbers = () => {
        for (const number of document.querySelectorAll?.(
            '[data-ticket-order-number]',
        ) ?? []) {
            number.style.fontSize = '52px';
            const available = number.parentElement.clientWidth;
            const width = number.getBoundingClientRect().width;
            if (available > 0 && width > available) {
                number.style.fontSize = `${((52 * available) / width) * 0.99}px`;
            }
        }
    };

    const nextAutomaticCopy = () => {
        if (!automatic) {
            return;
        }
        while (automaticIndex < copies.length) {
            const copy = copies[automaticIndex++];
            if (states.get(copy) === 'pending') {
                requestCopy(copy);
                return;
            }
        }
    };

    const scheduleNext = () => {
        if (target.setTimeout) {
            target.setTimeout(nextAutomaticCopy, 0);
        } else {
            nextAutomaticCopy();
        }
    };

    const requestCopy = (copy, manual = false) => {
        if (active !== null || !copies.includes(copy)) {
            return false;
        }
        if (manual) {
            automatic = false;
        }
        active = copy;
        if (document.body) {
            document.body.dataset.ticketActiveCopy = copy;
        }
        if (label) {
            label.textContent = `Copia: ${copyName(copy)}`;
        }
        if (status) {
            status.textContent = `Solicitando impresión: ${copyName(copy)}.`;
        }
        remember(copy, 'printing');
        updateControls();
        fitOrderNumbers();
        try {
            target.print();
        } catch {
            remember(copy, 'failed');
            active = null;
            if (status) {
                status.textContent = `No se pudo solicitar la copia ${copyName(copy)}. Reintente esa copia.`;
            }
            updateControls();
            scheduleNext();
        }
        return true;
    };

    const controller = {
        states,
        requestCopy: (copy) => requestCopy(copy, true),
    };
    printControllers.set(target, controller);
    target.addEventListener('afterprint', () => {
        if (active === null) {
            return;
        }
        remember(active, 'requested');
        active = null;
        if (status) {
            status.textContent =
                'Diálogo cerrado. Compruebe la impresión física; si falta una copia, imprima solo esa copia.';
        }
        updateControls();
        if (
            automatic &&
            target.name === popupName &&
            target.location.hash === popupFragment &&
            copies.every((copy) => states.get(copy) === 'requested')
        ) {
            target.close();
            return;
        }
        scheduleNext();
    });
    for (const button of buttons) {
        button.addEventListener('click', () =>
            requestCopy(button.dataset.ticketCopy, true),
        );
    }
    updateControls();

    const print = () => {
        fitOrderNumbers();
        if (target.location.hash === manualPopupFragment) {
            automatic = false;
        }
        if (document.fonts && document.fonts.status !== 'loaded') {
            document.fonts.ready.then(nextAutomaticCopy);
        } else {
            nextAutomaticCopy();
        }
    };
    if (target.document.readyState === 'complete') {
        print();
    } else {
        target.addEventListener('load', print, { once: true });
    }
    return controller;
}

if (
    typeof window !== 'undefined' &&
    window.document.body?.hasAttribute('data-pos-ticket-print')
) {
    installTicketPrint();
}
